import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  axiosGet,
  axiosPost,
  reactQueryDefaultOptions,
} from "../helpers/baseApi";
import { SERVICE_URLS } from "../helpers/serviceUrls";
import { QUERY_KEYS } from "../helpers/queryKeys";

// Sales Admin Mapping: which BDMs a PrismHR "Sales Admin" user supports. A
// mapped Sales Admin's client visibility is scoped to their BDMs' clients
// (or all clients, when allowAllClients is set).
//
// Unlike Report Exclusions, this list is global - there is no OE period - so
// the query below has no `enabled` guard.

// Rows for the admin grid.
export const useQueryGetSalesAdminMappings = (includeInactive = false) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.SALES_ADMIN_MAPPING_LIST_GET(includeInactive)
    );
  };

  return useQuery(
    [QUERY_KEYS.SALES_ADMIN_MAPPING_LIST, includeInactive],
    getData,
    {
      ...reactQueryDefaultOptions,
    }
  );
};

// PrismHR internal users available to pick as a Sales Admin or a BDM.
export const useQueryGetPrismInternalUsers = () => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.SALES_ADMIN_MAPPING_USERS_GET);
  };

  return useQuery([QUERY_KEYS.PRISM_INTERNAL_USERS], getData, {
    ...reactQueryDefaultOptions,
  });
};

const invalidateSalesAdminMappings = (queryClient) => {
  // Both the active and the show-removed list are stale after a write, so
  // invalidate on the bare key rather than a key with extra segments.
  queryClient.invalidateQueries([QUERY_KEYS.SALES_ADMIN_MAPPING_LIST]);
};

/**
 * Bulk add / reactivate BDMs under one Sales Admin.
 *
 * Resolves with { addedCount, reactivatedCount, alreadyMappedCount,
 * invalidCount, mappedElsewhereCount, outcomes }. A BDM already mapped to a
 * different Sales Admin comes back as mappedElsewhereCount rather than an
 * error.
 */
export const useMutatePostSalesAdminMapping = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.SALES_ADMIN_MAPPING_SAVE_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => invalidateSalesAdminMappings(queryClient),
  });
};

// Removes one BDM from under one Sales Admin. Resolves with true when a row
// was actually removed; an already-removed mapping resolves false rather
// than throwing.
export const useMutatePostSalesAdminMappingRemove = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.SALES_ADMIN_MAPPING_REMOVE_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => invalidateSalesAdminMappings(queryClient),
  });
};

// Removes every BDM mapped under one Sales Admin. Resolves with the count of
// rows removed.
export const useMutatePostSalesAdminMappingRemoveAll = () => {
  const postData = async (data) => {
    return await axiosPost(
      SERVICE_URLS.SALES_ADMIN_MAPPING_REMOVE_ALL_POST,
      data
    );
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => invalidateSalesAdminMappings(queryClient),
  });
};

/**
 * Resolves one PrismHR user by id and caches them server-side, backing the
 * "Add by user id" affordance on the mapping pickers.
 *
 * The pickers read PrismUserDetail, which the background sync only populates
 * from ClientAssignment's SalesPerson/BenefitRep columns - so a newly created
 * sales-support user is absent from the list entirely. This fills that gap.
 *
 * Resolves with { found, user }. An unknown id is found: false, NOT an error -
 * "no such user" is a normal answer to an admin typing into a text box. On a
 * hit, the users query is invalidated so the person appears in the grid and in
 * every future dropdown, for everyone.
 */
export const useMutatePostResolvePrismUser = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.SALES_ADMIN_MAPPING_RESOLVE_USER_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (result) => {
      if (result?.data?.found) {
        queryClient.invalidateQueries([QUERY_KEYS.PRISM_INTERNAL_USERS]);
      }
    },
  });
};
